import { all, call, fork, put, takeEvery, select, delay } from 'redux-saga/effects'
import * as types from 'store/types'
import * as authApi from 'store/apis'
import * as storageUtils from 'utils/storageUtils'
import { push } from 'connected-react-router'
import { cloneDeep } from 'lodash'
import { consoleLog, getFirebase, getToken, handleApiError, logAmplitudeEvent } from 'utils/helpers'
import {
  toggleSettingsModal_action,
  reseTeams_action,
  resetCompany_action,
  resetSubsription_action,
  resetContent_action,
  toggleContentPermissionModal_action,
  resetTemplates_action,
  toggleTemplatesModal_action,
  toggleCreateItemsPopup_action,
  setLoggedOutUser,
  setFCMloaded,
  setFCMToken,
  setLoggedInUser
} from 'store/actions'

export const getQuery = (state) => state.router.location.query
export const getCurrentRoute = (state) => state.router?.location?.pathname

function* setupFCM_saga() {
  const { is_fcm_loaded } = yield select((state) => state.auth.user)
  if (!is_fcm_loaded) {
    yield put(setFCMloaded(true))
    try {
      const { firebase } = getFirebase()
      const messaging = firebase.messaging()
      var currentToken = yield call(getToken, messaging)
      if (currentToken) {
        yield put(setFCMToken(currentToken))
        yield call(authApi.updateFCMToken_api, { device_type: 'web', device_token: currentToken })
      }
      messaging.onTokenRefresh(function* () {
        currentToken = yield call(getToken, messaging)
        if (currentToken) {
          yield put(setFCMToken(currentToken))
          yield call(authApi.updateFCMToken_api, { device_type: 'web', device_token: currentToken })
        }
      })
    } catch (error) {
      if (error) {
        consoleLog(error)
      }
    }
  }
}
function* setAuthentication(data, keepModalsOpened = true, noRedirect = false) {
  const user = cloneDeep(data)
  delete user.token
  const userData = {
    token: data.token,
    user
  }
  yield put(setLoggedInUser(userData))
  yield call(storageUtils.setUserToken, data.token)
  yield call(storageUtils.setUserData, user)
  if (!keepModalsOpened) {
    yield put(toggleSettingsModal_action(false))
    yield put(toggleTemplatesModal_action(false))
    yield put(toggleContentPermissionModal_action(false))
  }

  if (window.clarity) {
    window.clarity('set', 'email', user.email)
  }

  if (!noRedirect) {
    yield delay(100)
    const pathname = yield select(getCurrentRoute)
    const query = yield select(getQuery)
    let queryString = ''
    if (Object.keys(query).length > 0) {
      queryString = Object.keys(query)
        .reduce((p, c) => `${p}${c}=${query[c]}&`, '?')
        .slice(0, -1)
    }
    if (!user.membership || !user.member_code) {
      yield put(push('/onboarding'))
    } else if (user.membership?.next_onboarding_step) {
      yield put(push(`/onboarding?is_company=${user.membership.onboarding_is_company}&continue=${user.membership.next_onboarding_step}`))
    } else if (pathname.includes('/onboarding') || queryString.includes('invited')) {
      yield put(push('/'))
      yield delay(2000)
      if (queryString.includes('invited')) {
        yield put(toggleCreateItemsPopup_action(true))
      }
    } else if (!pathname.includes('/login') && !pathname.includes('/register') && !pathname.includes('/onboarding') && !pathname.includes('/social-login')) {
      yield put(push(`${pathname || '/'}${queryString}`))
    } else {
      yield put(push(`/${queryString}`))
    }
  }
}

function* doLogin_saga({ data, resolve, reject }) {
  try {
    const { email, password, invite_token } = data
    const result = yield call(authApi.login_api, { email, password, invite_token })
    yield setAuthentication(result)
    logAmplitudeEvent('Logged in regular')
    resolve && resolve()
  } catch (error) {
    handleApiError(error)
    reject && reject()
  }
}
function* doSocialLogin_saga({ data, resolve, reject }) {
  try {
    const result = yield call(authApi.socialLogin_api, data)
    yield setAuthentication(result)
    if (process.env.REACT_APP_NODE_ENV === 'production') {
      if (result.is_new_register) {
        logAmplitudeEvent('Signed up via Google')
        window.gtag('event', 'sign_up')
        window.gtag('event', 'conversion', { send_to: 'AW-848665564/RMPsCIWN-7kDENy31pQD' })
      } else {
        logAmplitudeEvent('Logged in via Google')
      }
    }
    resolve && resolve()
  } catch (error) {
    handleApiError(error)
    reject && reject()
  }
}
function* doRegister_saga({ data, resolve, reject }) {
  try {
    const result = yield call(authApi.register_api, data)
    yield setAuthentication(result)
    if (process.env.REACT_APP_NODE_ENV === 'production') {
      logAmplitudeEvent('Signed up')
      window.gtag('event', 'sign_up')
      window.gtag('event', 'conversion', { send_to: 'AW-848665564/RMPsCIWN-7kDENy31pQD' })
    }
    resolve && resolve()
  } catch (error) {
    handleApiError(error)
    reject && reject()
  }
}
function* logoutUser() {
  try {
    logAmplitudeEvent('Logged out')
    yield call(storageUtils.unsetLocalStorageValue, 'company')
    yield call(storageUtils.unsetLocalStorageValue, 'mainmenu_width')
    yield call(storageUtils.unsetLocalStorageValue, 'userData')
    yield call(storageUtils.unsetLocalStorageValue, 'traffic_source')
    yield put(setLoggedOutUser())
    yield put(reseTeams_action())
    yield put(resetCompany_action())
    yield put(resetContent_action())
    yield put(resetTemplates_action())
    yield put(resetSubsription_action())
    yield call(authApi.logout)
    yield call(storageUtils.unsetLocalStorageValue, 'token')
    if (window.amplitude) {
      window.amplitude.getInstance().setUserId(null)
      window.amplitude.getInstance().regenerateDeviceId()
    }
    yield put(push('/login'))
  } catch (err) {
    handleApiError(err)
  }
}
function* restoreSession({ keepModalsOpened }) {
  const query = yield select(getQuery)
  let queryString = ''
  if (Object.keys(query).length > 0) {
    queryString = Object.keys(query)
      .reduce((p, c) => `${p}${c}=${query[c]}&`, '?')
      .slice(0, -1)
  }

  const pathname = yield select(getCurrentRoute)
  if (pathname.includes('register') || (pathname.includes('register') && queryString.includes('invited'))) {
    yield put(push(`/register${queryString}`))
  } else {
    let token = yield call(storageUtils.getUserToken)
    if (token && token !== '') {
      try {
        // Get user details
        const user = yield call(authApi.getUserProfile_api)
        user.token = token
        yield setAuthentication(user, keepModalsOpened)
        // if(pathname.includes('/guest')) yield put(push(`/page${queryString}`))
      } catch (error) {
        if (!pathname.includes('/guest')) {
          yield put(push(`/login${!queryString.includes('auth') ? queryString : ''}`))
        }
      }
    } else if (!pathname.includes('/guest') && !pathname.includes('/social-login')) {
      yield put(push(`/login${!queryString.includes('auth') ? queryString : ''}`))
    }
  }
}

function* getUserProfile_saga() {
  try {
    const user = yield call(authApi.getUserProfile_api)
    let token = yield call(storageUtils.getUserToken)
    user.token = token
    yield setAuthentication(user, false, true)
  } catch (error) {}
}

export function* watchLoginUser() {
  yield takeEvery(types.SOCIAL_LOGIN, doSocialLogin_saga)
  yield takeEvery(types.LOGIN_USER, doLogin_saga)
  yield takeEvery(types.GET_USER_PROFILE, getUserProfile_saga)
  yield takeEvery(types.REGISTER_USER, doRegister_saga)
  yield takeEvery(types.LOGOUT_USER, logoutUser)
  yield takeEvery(types.RESTORE_SESSION, restoreSession)
  yield takeEvery(types.SETUP_FCM, setupFCM_saga)
}

export default function* authSaga() {
  yield all([fork(watchLoginUser)])
}
